/**
 * Request to join (design: apply.html) for a first-time visitor whose email was just verified (applicant session).
 * Submits exactly what today's application sends (POST /api/auth/submit-application via today's intercomService).
 * Phone: Caryn's server switches SMS codes OFF for applications on purpose ("confirmed via re-entry only",
 * auth-service authRoutes.js) - so "Verify" here asks to type the number once more (today's site only fakes a check).
 * After sending (or when a pending applicant comes back): the application number + today's "request expedited review"
 * form (legacy ExpeditedReviewForm, unchanged: POST /api/auth/submit-expedited-review), as on today's site.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import { useAuth } from '@/features/auth'
import { submitApplication, getApplicationNumber } from '@/services/intercom/intercomService'
import { ExpeditedReviewForm } from '@/components/auth/ExpeditedReviewForm'
import { CONFIRMATION_MESSAGE } from '@/config/brandConfig'
import { SUPPORT_EMAIL } from '@/config/brandConfig'
import { applySprite } from '~/design/sprites'
import { Sprite } from '~/app/ui'
import { Falling } from './Falling'

const I = ({ id }: { id: string }) => <svg className="icon" aria-hidden="true"><use href={`#${id}`} /></svg>
const toE164 = (raw: string) => {
  const v = raw.trim()
  try { const p = parsePhoneNumber(v.startsWith('+') ? v : v, 'US'); return p && isValidPhoneNumber(p.number) ? p.number : null } catch { return null }
}

export function Apply() {
  const { applicant, user, logoutApplicant } = useAuth()
  const navigate = useNavigate()
  const [first, setFirst] = useState(applicant?.firstName || '')
  const [last, setLast] = useState(applicant?.lastName || '')
  const [phone, setPhone] = useState(applicant?.phoneNumber || '')
  const [phoneStep, setPhoneStep] = useState<'open' | 'confirm' | 'done'>('open')
  const [again, setAgain] = useState('')
  const [why, setWhy] = useState('')
  const [age, setAge] = useState(false)
  const [errs, setErrs] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  // undefined = the form; a number/null = sent. A returning applicant whose application is pending sees "sent" (as today).
  const [sent, setSent] = useState<number | null | undefined>(applicant?.status === 'pending' ? null : undefined)
  const [expedite, setExpedite] = useState<'offer' | 'form' | 'done'>('offer')
  const [expediteStart, setExpediteStart] = useState<number | null>(null)
  const appId = (applicant as any)?.applicationId as string | undefined
  useEffect(() => {
    if (sent !== null || !appId) return
    getApplicationNumber(appId).then((r) => { if (r) { setSent(r.applicationNumber ?? null); if (r.expeditedReviewSubmitted) setExpedite('done') } })
  }, [sent, appId])

  if (user) return <Navigate to="/home" replace />
  if (!applicant) return <Navigate to="/" replace />

  const verifyPhone = () => {
    if (!toE164(phone)) { setErrs((e) => ({ ...e, phone: 'Please enter a valid phone number (with the country code if outside the US).' })); return }
    setErrs((e) => ({ ...e, phone: '' })); setAgain(''); setPhoneStep('confirm')
  }
  const confirmPhone = () => {
    if (toE164(again) !== toE164(phone)) { setErrs((e) => ({ ...e, phone: 'Those two numbers don’t match. Please check and try again.' })); return }
    setErrs((e) => ({ ...e, phone: '' })); setPhoneStep('done')
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const n: Record<string, string> = {}
    if (!first.trim()) n.first = 'Please enter your first name.'
    if (!last.trim()) n.last = 'Please enter your last name.'
    if (phoneStep !== 'done') n.phone = 'Please confirm your phone number.'
    if (why.trim().length < 10) n.why = 'Please tell me a little more (at least 10 characters).'
    if (!age) n.age = 'Please confirm you are 18 or older.'
    setErrs(n)
    if (Object.keys(n).length) return
    setBusy(true)
    const name = `${first.trim()} ${last.trim()}`
    const r = await submitApplication({ firstName: first.trim(), lastName: last.trim(), name, email: applicant!.email, reason: why.trim(), phoneNumber: toE164(phone) || phone, applicationId: (applicant as any).applicationId } as any)
    setBusy(false)
    if (!r.success) { setErrs({ form: r.error || 'Something went wrong. Please try again.' }); return }
    localStorage.setItem('applicant_data', JSON.stringify({ ...applicant, status: 'pending', name, firstName: first.trim(), lastName: last.trim(), phoneNumber: toE164(phone) }))
    setSent(r.applicationNumber ?? null)
  }
  const leave = () => { logoutApplicant(); navigate('/') }

  return (
    <div className="cw-apply">
      <Sprite svg={applySprite} />
      <Falling />
      <main className="stage">
        <section className="copy">
          <a className="wordmark" href="/" onClick={(e) => { e.preventDefault(); leave() }} aria-label="caryn’s world, back to the enter page">caryn’s world <I id="i-heart" /></a>
          <p className="eyebrow">Invite-only community</p>
          <div className="flourish" aria-hidden="true"><I id="i-heart" /></div>
          <div className="card">
            {sent === undefined ? (
              <form className="form" noValidate onSubmit={submit}>
                <h1 style={{ textAlign: 'center' }}>Request to join</h1>
                <p className="note" style={{ textAlign: 'center' }}>Tell me a little about you, then I’ll take a look.</p>
                <div className="grid2">
                  <div className="field"><label htmlFor="first">First name</label><input className="input" id="first" autoComplete="given-name" value={first} onChange={(e) => setFirst(e.target.value)} /><p className="err">{errs.first}</p></div>
                  <div className="field"><label htmlFor="last">Last name</label><input className="input" id="last" autoComplete="family-name" value={last} onChange={(e) => setLast(e.target.value)} /><p className="err">{errs.last}</p></div>
                </div>
                <div className="field"><label htmlFor="email">Email</label>
                  <div className="locked"><input className="input" id="email" type="email" readOnly value={applicant.email} /><span className="pill"><I id="i-check" />Verified</span></div></div>
                <div className="field"><label htmlFor="phone">Phone</label>
                  {phoneStep === 'open' && (
                    <div className="phone-state"><div className="combo">
                      <input className="input" id="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 555-0123" value={phone} onChange={(e) => setPhone(e.target.value)} />
                      <button className="btn-sm" type="button" onClick={verifyPhone}>Verify</button></div></div>
                  )}
                  {phoneStep === 'confirm' && (
                    <div className="phone-code">
                      <p>Type <strong>{toE164(phone)}</strong> once more to confirm it.</p>
                      <div className="combo">
                        <input className="input" type="tel" inputMode="tel" autoComplete="off" aria-label="Phone number again" value={again} onChange={(e) => setAgain(e.target.value)} autoFocus />
                        <button className="btn-sm" type="button" onClick={confirmPhone}>Confirm</button>
                      </div>
                      <div className="row"><button className="link" type="button" onClick={() => setPhoneStep('open')}>edit number</button></div>
                    </div>
                  )}
                  {phoneStep === 'done' && (
                    <div className="phone-state">
                      <div className="locked"><input className="input" type="tel" readOnly tabIndex={-1} value={toE164(phone) || phone} aria-label="Confirmed phone number" /><span className="pill"><I id="i-check" />Confirmed</span></div>
                      <p className="helper"><button className="link" type="button" onClick={() => setPhoneStep('open')}>change number</button></p>
                    </div>
                  )}
                  <p className="err">{errs.phone}</p>
                </div>
                <div className="field"><label htmlFor="why">Why do you want to join?</label>
                  <textarea className="input" id="why" value={why} onChange={(e) => setWhy(e.target.value)} />
                  <p className={`count${why.trim().length >= 10 ? ' ok' : ''}`}><I id="i-check" /><span>{why.trim().length >= 10 ? 'Looks good' : 'At least 10 characters'}</span></p>
                  <p className="err">{errs.why}</p>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className={`check${errs.age ? ' invalid' : ''}`}><input type="checkbox" checked={age} onChange={(e) => setAge(e.target.checked)} />
                    <span className="box" aria-hidden="true"><I id="i-check" /></span><span>I confirm I am 18 years of age or older.</span></label>
                  <p className="err">{errs.age}</p>
                </div>
                <p className="err" role="alert">{errs.form}</p>
                <button className="btn" type="submit" disabled={busy}>{busy ? 'sending…' : 'submit application'} <I id="i-arrow" /></button>
                <p className="terms">by applying you agree to the <a href="/terms">terms of service</a> and <a href="/privacy">privacy policy</a></p>
                <p className="terms" style={{ marginTop: 4 }}><a className="back" href="/" onClick={(e) => { e.preventDefault(); leave() }}>← use a different email</a></p>
              </form>
            ) : (
              <div className="done">
                {expedite === 'form' && appId ? (
                  <div className="tw-scope v2-expedite">
                    <ExpeditedReviewForm applicationId={appId} applicantEmail={applicant.email} applicantFirstName={applicant.firstName}
                      applicantPhone={applicant.phoneNumber} startTime={expediteStart} onBack={() => setExpedite('offer')} onSubmitted={() => setExpedite('done')} />
                  </div>
                ) : (
                  <>
                    <div className="seal" aria-hidden="true"><I id="i-heart" /></div>
                    <h1 tabIndex={-1}>{sent ? `Application #${sent.toLocaleString()}` : 'Application sent'}</h1>
                    <p className="note">{CONFIRMATION_MESSAGE || 'Thank you. I’ll be in touch by email soon.'}</p>
                    {expedite === 'done'
                      ? <p className="note v2-expedite-done"><I id="i-check" /> Expedited review submitted</p>
                      : appId && (
                        <>
                          <p className="note">Want to move up in line? Fill out the full application.</p>
                          <button className="btn" type="button" style={{ marginTop: 0 }} onClick={() => { setExpediteStart(Date.now()); setExpedite('form') }}>request expedited review <I id="i-arrow" /></button>
                        </>
                      )}
                    <p className="terms" style={{ marginTop: 14 }}><a className="back" href="/" onClick={(e) => { e.preventDefault(); leave() }}>← back to enter page</a></p>
                  </>
                )}
              </div>
            )}
          </div>
          <a className="support" style={{ textDecoration: 'none' }} href={`mailto:${SUPPORT_EMAIL}`}><I id="i-help" />Support</a>
        </section>
        <div className="pumpkin"><img src="/design/pumpkin.webp" width={640} height={529} alt="A hand-drawn autumn pumpkin with a heart-shaped vine and leaf" /></div>
      </main>
    </div>
  )
}
